import { Link, useParams } from "react-router-dom";
import { guides } from "@/mocks/guides";
import type { Guide } from "@/types/content";
import PageMeta from "@/components/feature/PageMeta";
import Breadcrumbs from "@/components/feature/Breadcrumbs";
import SectionHeading from "@/components/feature/SectionHeading";

const SITE = "https://imgmmd.cn";

export default function GuideDetail() {
  const { id } = useParams();
  const guide = (guides as Guide[]).find((item) => item.id === id);

  if (!guide) {
    return (
      <>
        <PageMeta
          title="指南不存在或已下线 - 避雷哨"
          description="你访问的维权指南不存在或已下线，可返回指南列表继续阅读其他内容。"
          path="/guides"
        />
        <section className="mx-auto flex max-w-content flex-col items-center justify-center px-4 py-24 text-center md:px-6">
          <span className="flex h-14 w-14 items-center justify-center rounded-full bg-background-100 text-foreground-500">
            <i className="ri-book-open-line text-2xl" />
          </span>
          <h1 className="mt-4 font-heading text-xl font-bold text-foreground-950">这篇指南不存在或已下线</h1>
          <p className="mt-2 max-w-md font-body text-sm leading-relaxed text-foreground-600">
            内容可能已被合并或更新。你可以返回指南列表，继续阅读其他防骗与维权内容。
          </p>
          <Link
            to="/guides"
            className="mt-6 inline-flex items-center gap-1.5 whitespace-nowrap rounded-md bg-primary-500 px-5 py-2.5 font-label text-sm font-medium text-background-50 transition-colors hover:bg-primary-600 cursor-pointer"
          >
            <i className="ri-arrow-left-line" />
            返回指南列表
          </Link>
        </section>
      </>
    );
  }

  const related = (guides as Guide[]).filter((item) => item.id !== guide.id).slice(0, 3);

  const jsonLd = [
    {
      "@context": "https://schema.org",
      "@type": "BlogPosting",
      headline: guide.title,
      description: guide.summary,
      inLanguage: "zh-CN",
      dateModified: guide.updatedAt,
      articleSection: guide.category,
      keywords: `${guide.category},维权指南,防骗知识`,
      author: { "@type": "Organization", name: "避雷哨" },
      publisher: { "@type": "Organization", name: "避雷哨", url: `${SITE}/` },
      mainEntityOfPage: `${SITE}/guides/${guide.id}`,
    },
    {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      itemListElement: [
        { "@type": "ListItem", position: 1, name: "首页", item: `${SITE}/` },
        { "@type": "ListItem", position: 2, name: "维权指南", item: `${SITE}/guides` },
        { "@type": "ListItem", position: 3, name: guide.title, item: `${SITE}/guides/${guide.id}` },
      ],
    },
  ];

  return (
    <>
      <PageMeta
        title={`${guide.title} - 避雷哨维权指南`}
        description={guide.summary.slice(0, 150)}
        keywords={`${guide.category},维权指南,防骗知识,投诉流程`}
        path={`/guides/${guide.id}`}
        jsonLd={jsonLd}
      />

      <section className="border-b border-background-200 bg-gradient-to-b from-background-100 to-background-50">
        <div className="mx-auto max-w-content px-4 py-8 md:px-6 md:py-10">
          <Breadcrumbs items={[{ name: "维权指南", to: "/guides" }, { name: guide.title }]} />
          <div className="mt-5 flex flex-wrap items-center gap-2 font-label text-xs text-foreground-500">
            <span className="rounded-full bg-secondary-50 px-2.5 py-1 text-secondary-800">{guide.category}</span>
            <span className="flex items-center gap-1">
              <i className="ri-time-line" />
              {guide.readMinutes} 分钟读完
            </span>
            <span className="flex items-center gap-1">
              <i className="ri-refresh-line" />
              更新于 {guide.updatedAt}
            </span>
          </div>
          <h1 className="mt-4 max-w-4xl font-heading text-2xl font-black leading-snug text-foreground-950 md:text-[2rem]">
            {guide.title}
          </h1>
          <p className="mt-3 max-w-3xl font-body text-sm leading-relaxed text-foreground-600 md:text-base">
            {guide.summary}
          </p>
        </div>
      </section>

      <section className="mx-auto max-w-content px-4 py-10 md:px-6 md:py-12">
        <div className="grid grid-cols-1 gap-10 lg:grid-cols-[1.7fr_1fr] lg:gap-12">
          <article className="min-w-0">
            <div className="overflow-hidden rounded-lg border border-background-200 bg-background-100">
              <img
                src={guide.cover}
                alt={`${guide.title} 配图`}
                title={`${guide.title} - 维权指南`}
                className="h-56 w-full object-cover md:h-80"
              />
            </div>

            {guide.sections.map((section, index) => (
              <section key={section.heading} id={`section-${index}`} className="mt-9 scroll-mt-28">
                <h2 className="font-heading text-xl font-bold text-foreground-950">{section.heading}</h2>
                {section.paragraphs.map((paragraph) => (
                  <p key={paragraph} className="mt-3 font-body text-base leading-relaxed text-foreground-700">
                    {paragraph}
                  </p>
                ))}
                {section.bullets && (
                  <ul className="mt-4 space-y-2.5 rounded-lg bg-background-100 p-5">
                    {section.bullets.map((bullet) => (
                      <li key={bullet} className="flex items-start gap-3">
                        <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-primary-500 text-background-50">
                          <i className="ri-check-line text-sm" />
                        </span>
                        <span className="font-body text-sm leading-relaxed text-foreground-700">{bullet}</span>
                      </li>
                    ))}
                  </ul>
                )}
              </section>
            ))}

            <div className="mt-10 flex flex-col gap-4 rounded-lg border border-accent-200 bg-accent-50 p-5 sm:flex-row sm:items-center sm:justify-between md:p-6">
              <div>
                <h2 className="font-heading text-base font-bold text-foreground-950">还想查点别的？</h2>
                <p className="mt-1.5 font-body text-sm text-foreground-600">
                  去避雷名单搜一下平台名称，看看是否已有同类反馈。
                </p>
              </div>
              <Link
                to="/blacklist"
                className="inline-flex shrink-0 items-center justify-center gap-1.5 whitespace-nowrap rounded-md bg-accent-500 px-5 py-2.5 font-label text-sm font-medium text-background-50 transition-colors hover:bg-accent-600 cursor-pointer"
              >
                <i className="ri-search-line" />
                打开避雷名单
              </Link>
            </div>
          </article>

          <aside className="flex flex-col gap-6 lg:sticky lg:top-28 lg:self-start">
            <div className="rounded-lg border border-background-200 bg-background-50 p-5">
              <h2 className="font-heading text-base font-bold text-foreground-950">本文目录</h2>
              <nav aria-label="本文目录" className="mt-3 flex flex-col">
                {guide.sections.map((section, index) => (
                  <a
                    key={section.heading}
                    href={`#section-${index}`}
                    className="flex items-center gap-2 border-l-2 border-background-200 py-2 pl-3 font-label text-sm text-foreground-600 transition-colors hover:border-primary-400 hover:text-primary-700"
                  >
                    <i className="ri-arrow-right-s-line text-foreground-400" />
                    {section.heading}
                  </a>
                ))}
              </nav>
            </div>

            <div className="rounded-lg border border-background-200 bg-background-50 p-5">
              <h2 className="font-heading text-base font-bold text-foreground-950">相关指南</h2>
              <div className="mt-3 flex flex-col">
                {related.map((item) => (
                  <Link
                    key={item.id}
                    to={`/guides/${item.id}`}
                    className="group flex items-start justify-between gap-3 border-b border-background-100 py-3 last:border-0 cursor-pointer"
                  >
                    <span className="min-w-0">
                      <span className="block font-label text-sm font-medium leading-snug text-foreground-900 group-hover:text-primary-700">
                        {item.title}
                      </span>
                      <span className="mt-1 block font-label text-xs text-foreground-500">{item.category}</span>
                    </span>
                    <i className="ri-arrow-right-s-line mt-0.5 text-lg text-foreground-400 group-hover:text-primary-600" />
                  </Link>
                ))}
              </div>
            </div>
          </aside>
        </div>
      </section>

      <section className="border-t border-background-200 bg-background-100">
        <div className="mx-auto max-w-content px-4 py-12 md:px-6 md:py-16">
          <div className="flex flex-col gap-6 md:flex-row md:items-end md:justify-between">
            <SectionHeading eyebrow="继续阅读" title="换一篇，可能正好用得上" />
            <Link
              to="/guides"
              className="inline-flex shrink-0 items-center gap-1.5 whitespace-nowrap font-label text-sm font-medium text-primary-700 transition-colors hover:text-primary-800 cursor-pointer"
            >
              查看全部指南
              <i className="ri-arrow-right-line" />
            </Link>
          </div>
          <div className="mt-8 grid grid-cols-1 gap-5 md:grid-cols-3">
            {related.map((item) => (
              <Link
                key={item.id}
                to={`/guides/${item.id}`}
                className="group flex flex-col rounded-lg border border-background-200 bg-background-50 p-5 transition-colors hover:border-primary-300 cursor-pointer"
              >
                <span className="rounded-full bg-secondary-50 px-2.5 py-1 font-label text-xs text-secondary-800 self-start">
                  {item.category}
                </span>
                <h3 className="mt-3 font-heading text-base font-bold leading-snug text-foreground-950 group-hover:text-primary-700">
                  {item.title}
                </h3>
                <p className="mt-2 line-clamp-2 font-body text-sm leading-relaxed text-foreground-600">
                  {item.summary}
                </p>
              </Link>
            ))}
          </div>
        </div>
      </section>
    </>
  );
}